import {Component,Input,OnInit,OnChanges,OnDestroy,SimpleChanges,Output,EventEmitter} from '@angular/core';
import { Subscription } from 'rxjs';
import type { PlanEntry } from '../add-plan-dialog/add-plan-dialog.component';
import { TimeEntryForPlanningService } from '../time-entry-for-planning.service';

interface WeekColumn {
  label: string;
  startISO: string;
  endISO: string;
}

interface ConsultantRow {
  consultant: string;
  weekHours: number[];
  weekHasClash: boolean[];
  total: number;
}

@Component({
  selector: 'app-plan-calendar-view',
  templateUrl: './plan-calendar-view.component.html',
  styleUrls: ['./plan-calendar-view.component.scss']
})
export class PlanCalendarViewComponent implements OnInit, OnChanges, OnDestroy {

  @Input() entries: PlanEntry[] = [];

  @Output() filteredEntriesChanged = new EventEmitter<PlanEntry[]>();

  weekColumns: WeekColumn[] = [];
  rows: ConsultantRow[] = [];

  private clientFilter: string[] = [];
  private projectFilter: string[] = [];
  private employeeFilter: string[] = [];

  // Matches the service's showClashesSubject default (true).
  private showClashes = true;
  private showNonClashed = true;


  private dateRangeFilter: { start: string | null; end: string | null } = { start: null, end: null };

  private chargedFilter: 'All' | 'Charged' | 'Uncharged' = 'All';

  private subscriptions = new Subscription();

  constructor(private planningService: TimeEntryForPlanningService) {}

  // ----- @ Lifecycle hooks -----

  /**
   * Subscribes to the planning filters and rebuilds the calendar when they change.
   */
  ngOnInit(): void {
    this.subscriptions.add(
      this.planningService.clientFilter$.subscribe(value => {
        this.clientFilter = value;
        this.rebuild();
      })
    );

    this.subscriptions.add(
      this.planningService.projectFilter$.subscribe(value => {
        this.projectFilter = value;
        this.rebuild();
      })
    );

    this.subscriptions.add(
      this.planningService.employeeFilter$.subscribe(value => {
        this.employeeFilter = value;
        this.rebuild();
      })
    );

    this.subscriptions.add(
      this.planningService.dateRange$.subscribe(value => {
        this.dateRangeFilter = value;
        this.rebuild();
      })
    );

    this.subscriptions.add(
      this.planningService.showClashes$.subscribe(value => {
        this.showClashes = value;
        this.rebuild();
      })
    );

    this.subscriptions.add(
      this.planningService.showNonClashed$.subscribe(value => {
        this.showNonClashed = value;
        this.rebuild();
      })
    );

    this.subscriptions.add(
      this.planningService.chargedFilter$.subscribe(value => {
        this.chargedFilter = value;
        this.rebuild();
      })
    );
  }

  /**
   * On changes - rebuilds when the parent passes in a new set of entries
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entries']) {
      this.rebuild();
    }
  }

  /**
   * On destroy - unsubscribes from all filter streams to avoid leaks
   */
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // Applies the active filters to determine which entries are shown in the calendar.
  private applyFilters(startISO: string | null, endISO: string | null): PlanEntry[] {
    let result = this.entries;

    if (this.clientFilter.length > 0) {
      result = result.filter(entry => this.clientFilter.includes(entry.client));
    }

    if (this.projectFilter.length > 0) {
      result = result.filter(entry => this.projectFilter.includes(entry.project));
    }

    if (this.employeeFilter.length > 0) {
      result = result.filter(entry =>
        this.employeeFilter.some(
          selected =>
            selected.trim().toUpperCase() ===
            (entry.consultant || '').trim().toUpperCase()
        )
      );
    }

    if (startISO) {
      result = result.filter(entry => entry.date >= startISO);
    }

    if (endISO) {
      result = result.filter(entry => entry.date <= endISO);
    }

    if (this.chargedFilter === 'Charged') {
      result = result.filter(entry => entry.charge === 'Yes');
    } else if (this.chargedFilter === 'Uncharged') {
      result = result.filter(entry => entry.charge === 'No');
    }

    return result;
  }

// Rebuilds the week columns and per-consultant rows whenever the entries or
// any filter changes
private rebuild(): void {
  if (!this.dateRangeFilter.start || !this.dateRangeFilter.end) {
    this.weekColumns = [];
    this.rows = [];
    return;
  }

  this.weekColumns = this.buildWeekColumns(
    this.dateRangeFilter.start,
    this.dateRangeFilter.end
  );

  if (this.weekColumns.length === 0) {
    this.rows = [];
    return;
  }

  const expandedStart = this.weekColumns[0].startISO;
  const expandedEnd = this.weekColumns[this.weekColumns.length - 1].endISO;

  const filtered = this.applyFilters(expandedStart, expandedEnd);

  // Clash detection deliberately ignores the client/project filters
  // (date + employee only), so an entry that's clashing stays flagged
  // even if the clashing counterpart is currently hidden.
  const clashBasis = this.applyDateAndEmployeeOnly(
    expandedStart,
    expandedEnd
  );

  // Four-state clash visibility:
  // both checked = no restriction
  // Show Clashes only = clashing entries
  // Show Entries only = non-clashing entries
  // neither checked = nothing
  const clashDates =
    this.planningService.getClashKeysFromEntries(this.entries);

  const applyClashState = (list: PlanEntry[]): PlanEntry[] => {
    if (this.showClashes && this.showNonClashed) {
      return list;
    }

    if (this.showClashes) {
      return list.filter(entry =>
        !entry.isPlaceholder &&
        clashDates.has(
          this.planningService.clashKey(entry.consultant, entry.date)
        )
      );
    }

    if (this.showNonClashed) {
      return list.filter(entry =>
        !entry.isPlaceholder &&
        !clashDates.has(
          this.planningService.clashKey(entry.consultant, entry.date)
        )
      );
    }

    return [];
  };
  // Send the filtered entries to the parent so Total Hours is updated.
  const filteredWithClashState = applyClashState(filtered);
  this.filteredEntriesChanged.emit(filteredWithClashState);

  // Keep clash detection based on the date/employee set so the clash
  // indicator is independent of the Show Clashes checkbox.
  const clashCausingIds =
    this.planningService.getClashCausingEntryIds(clashBasis);

  const clashByConsultantDate = new Map<string, Set<string>>();

  for (const entry of clashBasis) {
    if (!clashCausingIds.has(entry.id)) {
      continue;
    }

    if (!clashByConsultantDate.has(entry.consultant)) {
      clashByConsultantDate.set(entry.consultant, new Set());
    }

    clashByConsultantDate.get(entry.consultant)!.add(entry.date);
  }

  // consultant -> ISO date string -> hours
  const byConsultant = new Map<string, Map<string, number>>();

  for (const entry of filteredWithClashState) {
    if (entry.isPlaceholder) {
      continue;
    }

    const day = new Date(`${entry.date}T00:00:00`).getDay();

    if (day === 0 || day === 6) {
      continue;
    }

    if (!byConsultant.has(entry.consultant)) {
      byConsultant.set(entry.consultant, new Map());
    }

    const dayMap = byConsultant.get(entry.consultant)!;

    dayMap.set(
      entry.date,
      (dayMap.get(entry.date) ?? 0) + Number(entry.hours || 0)
    );
  }

  this.rows = [];

  for (const [consultant, dayMap] of byConsultant.entries()) {
    const weekHours: number[] = [];
    const weekHasClash: boolean[] = [];
    let rowTotal = 0;

    const consultantClashDates =
      clashByConsultantDate.get(consultant);

    for (const week of this.weekColumns) {
      let weekSum = 0;
      let clash = false;

      for (const [dateStr, hours] of dayMap.entries()) {
        if (
          dateStr >= week.startISO &&
          dateStr <= week.endISO
        ) {
          weekSum += hours;

          if (consultantClashDates?.has(dateStr)) {
            clash = true;
          }
        }
      }

      weekHours.push(weekSum);

      // Clash indicator is independent of Show Clashes.
      weekHasClash.push(clash);

      rowTotal += weekSum;
    }

    this.rows.push({
      consultant,
      weekHours,
      weekHasClash,
      total: rowTotal
    });
  }
}

  // Same as applyFilters(), but limited to date range + employee - used as the
  // basis for clash detection so the client/project filters don't hide real clashes
  private applyDateAndEmployeeOnly(startISO: string | null, endISO: string | null): PlanEntry[] {
    let result = this.entries;

    if (this.employeeFilter.length > 0) {
      result = result.filter(entry =>
        this.employeeFilter.some(
          selected =>
            selected.trim().toUpperCase() ===
            (entry.consultant || '').trim().toUpperCase()
        )
      );
    }

    if (startISO) {
      result = result.filter(entry => entry.date >= startISO);
    }

    if (endISO) {
      result = result.filter(entry => entry.date <= endISO);
    }

    return result;
  }

  // Builds Mon–Fri week columns spanning the selected date range, starting
  // from the Monday on/before periodStart
  private buildWeekColumns(periodStartISO: string, periodEndISO: string): WeekColumn[] {
    const weeks: WeekColumn[] = [];

    const periodStart = new Date(`${periodStartISO}T00:00:00`);
    const periodEnd = new Date(`${periodEndISO}T00:00:00`);

    const cursor = this.mondayOf(periodStart);
    let weekNum = 1;

    while (cursor <= periodEnd) {
      const weekStart = new Date(cursor);
      const weekEnd = new Date(cursor);
      weekEnd.setDate(weekEnd.getDate() + 4);

      const weekStartISO = this.toISODate(weekStart);
      const weekEndISO = this.toISODate(weekEnd);

      weeks.push({
        label: `Week ${weekNum} (${this.formatShort(weekStart)}–${this.formatShort(weekEnd)})`,
        startISO: weekStartISO,
        endISO: weekEndISO
      });

      cursor.setDate(cursor.getDate() + 7);
      weekNum++;
    }

    return weeks;
  }

  // Returns the Monday of the week containing the given date, with time zeroed out
  private mondayOf(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  // Formats a Date as YYYY-MM-DD using local date parts
  private toISODate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Formats a date for the week label, e.g. "5 Jun"
  private formatShort(date: Date): string {
    return `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}`;
  }
}